import { env } from './env';
import { sessionStore } from './session-store';
import type {
  AddLinkBody,
  AddLinkResult,
  AdminFailuresResponse,
  ApiErrorEnvelope,
  AppSettings,
  Category,
  CreateCategoryBody,
  CurrentUser,
  CursorPage,
  FacetValue,
  MediaChannelOption,
  MediaFacets,
  MediaItem,
  ItemsResponse,
  LinkDetail,
  LinkSummary,
  ListMediaParams,
  ListLinksParams,
  Stats,
  UpdateCategoryBody,
  UpdateLinkBody,
  UpdateMediaBody,
  UpdateSettingsBody,
  UploadScreenshotBody,
} from '@/types/api';

/** Every non-2xx response from the API surfaces as one of these. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string;
    details?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Raised when the request never reached the API (offline, DNS, CORS preflight failure). */
export class NetworkError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

type QueryValue = string | number | boolean | null | undefined | readonly string[];
export type QueryParams = Record<string, QueryValue>;

/** Repeats array values as `?category=a&category=b`; drops empty/undefined entries. */
export function buildQueryString(params: QueryParams | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== undefined && entry !== null && entry !== '') search.append(key, entry);
      }
      continue;
    }
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function isErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = (value as { error?: unknown }).error;
  if (typeof candidate !== 'object' || candidate === null) return false;
  const err = candidate as Record<string, unknown>;
  return typeof err.code === 'string' && typeof err.message === 'string';
}

const DEFAULT_ERROR_MESSAGES: Record<number, string> = {
  400: 'The request was rejected as invalid.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'That resource could not be found.',
  409: 'That change conflicts with the current state.',
  429: 'Too many requests. Please slow down and try again.',
  500: 'The server hit an unexpected error.',
  502: 'The server is temporarily unavailable.',
  503: 'The server is temporarily unavailable.',
};

/** Turns a failed Response into a typed ApiError, tolerating non-JSON and malformed bodies. */
export async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    const text = await response.text();
    payload = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    payload = undefined;
  }

  if (isErrorEnvelope(payload)) {
    const { code, message, requestId, details } = payload.error;
    return new ApiError({
      status: response.status,
      code,
      message,
      requestId: typeof requestId === 'string' ? requestId : undefined,
      details,
    });
  }

  return new ApiError({
    status: response.status,
    code: `HTTP_${response.status}`,
    message:
      DEFAULT_ERROR_MESSAGES[response.status] ??
      `Request failed with status ${response.status}${response.statusText ? ` ${response.statusText}` : ''}.`,
  });
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Internal. Cleared on the retry that follows a successful token renewal, so a request can
   * never bounce between "renew" and "401" more than once.
   */
  renewOnUnauthorized?: boolean;
}

/**
 * Without a token, reads go to the unauthenticated mirror of the API.
 *
 * `/api/v1/*` is behind an API Gateway JWT authorizer, which rejects the request before it
 * reaches the Lambda — so an anonymous visitor has to ask elsewhere. `/api/public/v1/*` serves
 * the same catalogue with sharer identity redacted.
 */
function resolvePath(path: string, authenticated: boolean): string {
  if (authenticated) return path;
  return path.startsWith('/api/v1/') ? `/api/public/v1/${path.slice('/api/v1/'.length)}` : path;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, signal, renewOnUnauthorized = true } = options;
  const token = sessionStore.getIdToken();
  const url = `${env.apiBaseUrl}${resolvePath(path, Boolean(token))}${buildQueryString(query)}`;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError('Could not reach the SLC API. Check your connection and retry.', {
      cause,
    });
  }

  // A 401 usually just means the 60-minute id token aged out while the tab was idle. Ask the
  // auth layer to spend the refresh token and, if that works, replay the request once. The
  // original attempt was rejected by the API Gateway authorizer before reaching any Lambda, so
  // replaying a POST/PATCH/DELETE cannot duplicate a side effect.
  if (response.status === 401 && renewOnUnauthorized) {
    const renewed = await sessionStore.notifyUnauthorized();
    if (renewed) {
      return request<T>(path, { ...options, renewOnUnauthorized: false });
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

/** Typed surface of the SLC dashboard API. One method per documented endpoint. */
export const apiClient = {
  getMe(signal?: AbortSignal): Promise<CurrentUser> {
    return request<CurrentUser>('/api/v1/me', { signal });
  },

  listLinks(params: ListLinksParams, signal?: AbortSignal): Promise<CursorPage<LinkSummary>> {
    // Spread: an interface has no index signature, a fresh object literal does.
    return request<CursorPage<LinkSummary>>('/api/v1/links', { query: { ...params }, signal });
  },

  getLink(linkId: string, signal?: AbortSignal): Promise<LinkDetail> {
    return request<LinkDetail>(`/api/v1/links/${encodeURIComponent(linkId)}`, { signal });
  },

  updateLink(linkId: string, body: UpdateLinkBody): Promise<LinkDetail> {
    return request<LinkDetail>(`/api/v1/links/${encodeURIComponent(linkId)}`, {
      method: 'PATCH',
      body,
    });
  },

  addLink(body: AddLinkBody): Promise<AddLinkResult> {
    return request<AddLinkResult>('/api/v1/links', { method: 'POST', body });
  },

  deleteLink(linkId: string): Promise<void> {
    return request<void>(`/api/v1/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
  },

  retryLink(linkId: string): Promise<LinkDetail> {
    return request<LinkDetail>(`/api/v1/links/${encodeURIComponent(linkId)}/retry`, {
      method: 'POST',
    });
  },

  regenerateScreenshot(linkId: string): Promise<LinkDetail> {
    return request<LinkDetail>(`/api/v1/links/${encodeURIComponent(linkId)}/screenshot`, {
      method: 'POST',
    });
  },

  uploadScreenshot(linkId: string, body: UploadScreenshotBody): Promise<LinkDetail> {
    return request<LinkDetail>(`/api/v1/links/${encodeURIComponent(linkId)}/screenshot/upload`, {
      method: 'POST',
      body,
    });
  },

  listCategories(signal?: AbortSignal): Promise<ItemsResponse<Category>> {
    return request<ItemsResponse<Category>>('/api/v1/categories', { signal });
  },

  createCategory(body: CreateCategoryBody): Promise<Category> {
    return request<Category>('/api/v1/categories', { method: 'POST', body });
  },

  updateCategory(categoryId: string, body: UpdateCategoryBody): Promise<Category> {
    return request<Category>(`/api/v1/categories/${encodeURIComponent(categoryId)}`, {
      method: 'PATCH',
      body,
    });
  },

  deleteCategory(categoryId: string): Promise<void> {
    return request<void>(`/api/v1/categories/${encodeURIComponent(categoryId)}`, {
      method: 'DELETE',
    });
  },

  listUsers(signal?: AbortSignal): Promise<ItemsResponse<FacetValue>> {
    return request<ItemsResponse<FacetValue>>('/api/v1/users', { signal });
  },

  listChannels(signal?: AbortSignal): Promise<ItemsResponse<FacetValue>> {
    return request<ItemsResponse<FacetValue>>('/api/v1/channels', { signal });
  },

  listDomains(signal?: AbortSignal): Promise<ItemsResponse<FacetValue>> {
    return request<ItemsResponse<FacetValue>>('/api/v1/domains', { signal });
  },

  getAdminFailures(signal?: AbortSignal): Promise<AdminFailuresResponse> {
    return request<AdminFailuresResponse>('/api/v1/admin/failures', { signal });
  },

  getStats(signal?: AbortSignal): Promise<Stats> {
    return request<Stats>('/api/v1/stats', { signal });
  },

  getSettings(signal?: AbortSignal): Promise<AppSettings> {
    return request<AppSettings>('/api/v1/settings', { signal });
  },

  updateSettings(body: UpdateSettingsBody): Promise<AppSettings> {
    return request<AppSettings>('/api/v1/settings', { method: 'PATCH', body });
  },

  listMediaChannelOptions(signal?: AbortSignal): Promise<ItemsResponse<MediaChannelOption>> {
    return request<ItemsResponse<MediaChannelOption>>('/api/v1/settings/media-channels', {
      signal,
    });
  },

  listMedia(params: ListMediaParams, signal?: AbortSignal): Promise<CursorPage<MediaItem>> {
    return request<CursorPage<MediaItem>>('/api/v1/media', { query: { ...params }, signal });
  },

  getMediaFacets(signal?: AbortSignal): Promise<MediaFacets> {
    return request<MediaFacets>('/api/v1/media/facets', { signal });
  },

  updateMedia(mediaId: string, body: UpdateMediaBody): Promise<MediaItem> {
    return request<MediaItem>(`/api/v1/media/${encodeURIComponent(mediaId)}`, {
      method: 'PATCH',
      body,
    });
  },

  /** Queues a new generated title and description. Answers before it is written. */
  describeMedia(mediaId: string): Promise<{ queued: boolean }> {
    return request<{ queued: boolean }>(`/api/v1/media/${encodeURIComponent(mediaId)}/describe`, {
      method: 'POST',
    });
  },

  deleteMedia(mediaId: string): Promise<void> {
    return request<void>(`/api/v1/media/${encodeURIComponent(mediaId)}`, { method: 'DELETE' });
  },
};

export type ApiClient = typeof apiClient;

/** Human-readable message for anything a query/mutation can reject with. */
/**
 * Formats the field-level issues the API returns alongside a validation error into a short,
 * human-readable string, e.g. "description: String must contain at most 2000 character(s)". Returns
 * undefined when `details` is not the array of `{ path, message }` issues the API sends.
 */
function validationIssues(details: unknown): string | undefined {
  if (!Array.isArray(details)) return undefined;
  const parts = details
    .map((issue) => {
      if (typeof issue !== 'object' || issue === null) return '';
      const { path, message } = issue as { path?: unknown; message?: unknown };
      if (typeof message !== 'string' || message.length === 0) return '';
      const label = typeof path === 'string' && path.length > 0 ? `${path}: ` : '';
      return `${label}${message}`;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join('; ') : undefined;
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    // Surface which field failed and why — otherwise every validation error reads "Request
    // validation failed" and the user cannot tell what to fix.
    const issues = validationIssues(error.details);
    return issues ? `${error.message}: ${issues}` : error.message;
  }
  if (error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}
